import { auth } from "../firebase-config";

export type BillingErrorCode =
  | "AUTH_REQUIRED"
  | "CONFIGURATION_ERROR"
  | "INVALID_RESPONSE"
  | "NETWORK_ERROR"
  | "REQUEST_FAILED"
  | "ALREADY_SUBSCRIBED"
  | "BILLING_REVIEW_REQUIRED"
  | "CUSTOMER_OWNERSHIP_MISMATCH"
  | "CUSTOMER_NOT_FOUND";

export interface BillingSession {
  url: string;
  requestId: string;
}

interface BillingErrorResponse {
  url?: unknown;
  code?: unknown;
  message?: unknown;
  requestId?: unknown;
  error?: {
    code?: unknown;
    message?: unknown;
  };
}

export class BillingError extends Error {
  readonly code: string;
  readonly retryable: boolean;
  readonly requestId?: string;

  constructor(
    code: string,
    message: string,
    retryable: boolean,
    requestId?: string,
  ) {
    super(message);
    this.name = "BillingError";
    this.code = code;
    this.retryable = retryable;
    this.requestId = requestId;
  }
}

const getBillingApiUrl = (): string => {
  const configuredUrl = process.env.REACT_APP_BILLING_API_URL?.trim();
  if (!configuredUrl) {
    throw new BillingError(
      "CONFIGURATION_ERROR",
      "Billing is not configured.",
      false,
    );
  }

  let parsed: URL;
  try {
    parsed = new URL(configuredUrl);
  } catch {
    throw new BillingError(
      "CONFIGURATION_ERROR",
      "Billing is not configured.",
      false,
    );
  }

  const localDevelopmentUrl =
    process.env.NODE_ENV === "development" &&
    parsed.protocol === "http:" &&
    (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1");

  if (parsed.protocol !== "https:" && !localDevelopmentUrl) {
    throw new BillingError(
      "CONFIGURATION_ERROR",
      "Billing is not configured.",
      false,
    );
  }

  return configuredUrl.replace(/\/+$/, "");
};

const parseResponseBody = async (
  response: Response,
): Promise<BillingErrorResponse | null> => {
  try {
    return (await response.json()) as BillingErrorResponse;
  } catch {
    return null;
  }
};

const requestBillingSession = async (
  path: "/v1/billing/checkout" | "/v1/billing/portal",
  body: Record<string, unknown>,
  expectedHostname: "checkout.stripe.com" | "billing.stripe.com",
): Promise<BillingSession> => {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) {
    throw new BillingError(
      "AUTH_REQUIRED",
      "Sign in to continue with billing.",
      false,
    );
  }

  const token = await firebaseUser.getIdToken();
  let response: Response;

  try {
    response = await fetch(`${getBillingApiUrl()}${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new BillingError(
      "NETWORK_ERROR",
      "We could not reach billing. Check your connection and try again.",
      true,
    );
  }

  const responseBody = await parseResponseBody(response);
  const requestId =
    typeof responseBody?.requestId === "string"
      ? responseBody.requestId
      : undefined;

  if (!response.ok) {
    const responseCode =
      (typeof responseBody?.error?.code === "string" &&
        responseBody.error.code) ||
      (typeof responseBody?.code === "string" && responseBody.code) ||
      "REQUEST_FAILED";
    const retryable =
      responseCode === "ALREADY_SUBSCRIBED" ||
      responseCode === "BILLING_REVIEW_REQUIRED" ||
      responseCode === "CUSTOMER_OWNERSHIP_MISMATCH" ||
      responseCode === "CUSTOMER_NOT_FOUND"
        ? false
        : response.status === 408 ||
          response.status === 429 ||
          response.status >= 500;
    throw new BillingError(
      responseCode,
      "Billing could not complete the request. Please try again.",
      retryable,
      requestId,
    );
  }

  if (
    typeof responseBody?.url !== "string" ||
    typeof responseBody.requestId !== "string"
  ) {
    throw new BillingError(
      "INVALID_RESPONSE",
      "Billing returned an invalid response. Please try again.",
      true,
      requestId,
    );
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(responseBody.url);
  } catch {
    throw new BillingError(
      "INVALID_RESPONSE",
      "Billing returned an invalid redirect. Please try again.",
      true,
      requestId,
    );
  }

  if (
    parsedUrl.origin !== `https://${expectedHostname}` ||
    parsedUrl.username !== "" ||
    parsedUrl.password !== ""
  ) {
    throw new BillingError(
      "INVALID_RESPONSE",
      "Billing returned an invalid redirect. Please try again.",
      true,
      requestId,
    );
  }

  return {
    url: parsedUrl.toString(),
    requestId: responseBody.requestId,
  };
};

export const createCheckoutSession = (
  attemptId: string,
): Promise<BillingSession> =>
  requestBillingSession(
    "/v1/billing/checkout",
    { attemptId },
    "checkout.stripe.com",
  );

export const createPortalSession = (): Promise<BillingSession> =>
  requestBillingSession("/v1/billing/portal", {}, "billing.stripe.com");

export const redirectToBillingUrl = (url: string): void => {
  window.location.assign(url);
};

export const getBillingErrorMessage = (error: unknown): string => {
  if (!(error instanceof BillingError)) {
    return "Billing could not complete the request. Please try again.";
  }

  if (error.code === "AUTH_REQUIRED") {
    return "Sign in to continue with billing.";
  }
  if (error.code === "CONFIGURATION_ERROR") {
    return "Billing is temporarily unavailable. Please contact support.";
  }
  if (error.code === "ALREADY_SUBSCRIBED") {
    const requestReference = error.requestId
      ? ` Request ID: ${error.requestId}.`
      : "";
    return `Your Pro subscription already exists, but access is still syncing. Refresh this page in a moment. If Pro does not appear, contact support so we can reconcile your account. Do not start another checkout.${requestReference}`;
  }
  if (error.code === "BILLING_REVIEW_REQUIRED") {
    const requestReference = error.requestId
      ? ` Request ID: ${error.requestId}.`
      : "";
    return `We found a billing issue that needs support review before you can continue. Please contact support.${requestReference}`;
  }
  if (error.code === "CUSTOMER_NOT_FOUND") {
    return "We could not find a billing account for this user. Please contact support.";
  }
  if (error.code === "CUSTOMER_OWNERSHIP_MISMATCH") {
    const requestReference = error.requestId
      ? ` Request ID: ${error.requestId}.`
      : "";
    return `This legacy billing account needs a one-time migration before it can be managed. Please contact support.${requestReference}`;
  }

  return error.message;
};
